import { useState } from "react";
import { Box, Button, Divider, Grid, Paper, Stack, TextField, Typography } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import moment from "moment";
import { toast } from "react-toastify";
import shiftManagementService from "services/shiftManagementService";
import { SAVE_BUTTON_SX } from "shifts/shiftStyles";
import { ShiftLogSheetResponse } from "shifts/models";

// Shift Log Sheet — the Flow port of the legacy screen. Enter a shift number or a control
// number to get a log sheet pre-filled with that shift's header; leave it blank for a blank
// sheet a driver can fill in by hand.

const ShiftLogSheet = () => {
  const [lookup, setLookup] = useState("");
  const [preview, setPreview] = useState<ShiftLogSheetResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handlePreview = async () => {
    setIsLoading(true);
    setPreview(null);
    try {
      const result = await shiftManagementService.getLogSheet(lookup.trim());
      if (!result.found) {
        toast.error("Shift Number or Control Number entered cannot be found.");
        return;
      }
      setPreview(result);
    } catch {
      toast.error("Could not look up that shift.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await shiftManagementService.getLogSheetPdf(lookup.trim());
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = lookup.trim() ? `ShiftLogSheet-${lookup.trim()}.pdf` : "ShiftLogSheet.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Shift Number or Control Number entered cannot be found.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box sx={{ px: 1, maxWidth: 900 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Shift Log Sheet
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Enter a shift number or control number to create a log sheet pre-populated with the shift header. Leave it
        blank to create a blank sheet.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            label="Shift Number or Control Number"
            value={lookup}
            onChange={(e) => setLookup(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handlePreview();
            }}
            size="small"
            sx={{ width: 300 }}
          />

          <Button variant="contained" color="info" onClick={handlePreview} disabled={isLoading || !lookup.trim()}>
            Look Up
          </Button>

          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleDownload}
            disabled={isDownloading}
            sx={SAVE_BUTTON_SX}
          >
            {isDownloading ? "Building…" : "Download PDF"}
          </Button>
        </Stack>
      </Paper>

      {preview && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Shift {preview.yardDailyId}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Field label="Date" value={preview.date ? moment(preview.date).format("MM/DD/YYYY") : ""} />
            <Field label="Location" value={preview.location} />
            <Field label="Shift Name" value={preview.shiftName} />
            <Field label="Shift Type" value={preview.shiftType} />
            <Field label="Control Number" value={preview.controlNumber} />
            <Field label="Driver" value={preview.driverName} />
            <Field label="Van Number" value={preview.unitNumber} />
            <Field label="Start Time" value={preview.timeStart} />
            <Field label="End Time" value={preview.timeEnd} />
            {/* Only meaningful when the driver is based somewhere other than the shift's hub. */}
            {preview.driverLocation && preview.driverLocation !== preview.location && (
              <Field label="Driver Location" value={preview.driverLocation} />
            )}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

const Field = ({ label, value }: { label: string; value: string }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      {label}
    </Typography>
    <Typography variant="body2">{value || "—"}</Typography>
  </Grid>
);

export default ShiftLogSheet;
